/**
 * Настройки (§3.6): секции-листы с якорями #vk, #images, #auto, #claude, #account.
 * Промпты в интерфейсе не правятся — это файлы prompts/*.md (правит владелец через Claude Code).
 * На десктопе слева липкая навигация по секциям, на узких экранах — лента разделов сверху.
 * Несохранённые правки в любой секции не теряются молча: уход со страницы — с подтверждением.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker, useLocation, useNavigate, useSearchParams } from "react-router";
import { Bot, CircleAlert, Image as ImageIcon, KeyRound, Link2, LogOut, Sparkles, UserRound, type LucideIcon } from "lucide-react";
import { api, errText } from "../lib/api";
import { useLogout, useMe } from "../lib/auth";
import { useSettings, useVkStatus } from "../lib/queries";
import { EcgLoader, EcgMark } from "../components/Ecg";
import { LoadError, PageHeader, useConfirm, useToast } from "../components/ui";
import { SetBlock, SetDirtyCtx, SetFacts, SetField, SetSection, setScrollTo, useSetDirty } from "../components/SettingsKit";
import { SettingsVk, vkTone, type SetTone } from "../components/SettingsVk";
import { SettingsAuto, SettingsClaude, SettingsImages } from "../components/SettingsServices";

const SECTIONS: { id: string; label: string; icon: LucideIcon }[] = [
  { id: "vk", label: "ВКонтакте", icon: Link2 },
  { id: "images", label: "Картинки", icon: ImageIcon },
  { id: "auto", label: "Автоконтент", icon: Sparkles },
  { id: "claude", label: "Claude", icon: Bot },
  { id: "account", label: "Аккаунт", icon: UserRound },
];
const TONE_TEXT: Record<SetTone, string> = { ok: "в порядке", warn: "нужно внимание", bad: "не подключено" };

export function SettingsPage() {
  const { data: s, error, isLoading, refetch } = useSettings();
  const { data: vk } = useVkStatus();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const ready = !!s;
  const [active, setActive] = useState(SECTIONS[0].id);
  const pinned = useRef(0);

  /* ---- несохранённые правки ---- */
  const dirty = useRef(new Set<string>());
  const report = useCallback((id: string, d: boolean) => {
    if (d) dirty.current.add(id);
    else dirty.current.delete(id);
  }, []);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty.current.size > 0 && currentLocation.pathname !== nextLocation.pathname,
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  const blockedKey = blocker.state === "blocked" ? blocker.location.key : null;
  useEffect(() => {
    if (!blockedKey) return;
    void confirm({
      title: "Уйти без сохранения?",
      text: "В настройках есть несохранённые изменения. Если уйти, они пропадут.",
      confirm: "Уйти без сохранения",
      danger: true,
    }).then((ok) => {
      const b = blockerRef.current;
      if (b.state !== "blocked") return;
      if (ok) {
        dirty.current.clear();
        b.proceed();
      } else b.reset();
    });
  }, [blockedKey, confirm]);
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current.size === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  /* ---- возврат из VK ID: ?vk=ok | ?vk=error&message=… ---- */
  const vkHandled = useRef(false);
  useEffect(() => {
    const flag = params.get("vk");
    if (!flag || vkHandled.current) return;
    vkHandled.current = true;
    if (flag === "ok") toast("Вы вошли через VK ID");
    else toast(params.get("message") || "Войти через VK ID не получилось. Нажмите «Войти через VK ID» ещё раз.", "error");
    void qc.invalidateQueries({ queryKey: ["vk"] });
    void qc.invalidateQueries({ queryKey: ["stats"] });
    void qc.invalidateQueries({ queryKey: ["settings"] });
    nav({ pathname: loc.pathname, search: "", hash: loc.hash || "#vk" }, { replace: true });
  }, [params, toast, qc, nav, loc.pathname, loc.hash]);

  /* ---- якорь из адреса (в том числе /settings#vk из баннера «Войти заново») ---- */
  useEffect(() => {
    if (!ready) return;
    const id = loc.hash.slice(1);
    if (!SECTIONS.some((x) => x.id === id)) return;
    const el = document.getElementById(id);
    if (!el) return;
    setActive(id);
    requestAnimationFrame(() => el.scrollIntoView({ block: "start" }));
  }, [ready, loc.hash]);

  /* ---- какая секция сейчас на экране ---- */
  useEffect(() => {
    if (!ready) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (Date.now() < pinned.current) return;
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 4) return setActive(SECTIONS[SECTIONS.length - 1].id);
      const line = window.matchMedia("(max-width: 1099px)").matches ? 140 : 120;
      let cur = SECTIONS[0].id;
      for (const x of SECTIONS) {
        const el = document.getElementById(x.id);
        if (el && el.getBoundingClientRect().top <= line) cur = x.id;
      }
      setActive(cur);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ready]);

  // на узком экране держим активный пункт ленты в поле зрения
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const box = navRef.current;
    const link = box?.querySelector<HTMLElement>(`[data-sec="${active}"]`);
    if (!box || !link || box.scrollWidth <= box.clientWidth) return;
    const left = link.offsetLeft - (box.clientWidth - link.offsetWidth) / 2;
    box.scrollTo({ left: Math.max(0, left), behavior: "auto" });
  }, [active]);

  const go = (id: string) => {
    setActive(id);
    pinned.current = Date.now() + 700;
    setScrollTo(document.getElementById(id));
    window.history.replaceState(window.history.state, "", `#${id}`);
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <EcgLoader label="Открываю настройки" />
      </div>
    );
  }
  if (error || !s) {
    return (
      <div className="page">
        <PageHeader title="Настройки" />
        <LoadError error={error ?? "Настройки не пришли с сервера"} retry={() => void refetch()} />
      </div>
    );
  }

  const tones: Record<string, SetTone | null> = {
    vk: vkTone(vk),
    images: s.image_provider !== "gemini-api" || s.gemini_key_set || s.gemini_mock ? "ok" : "warn",
    auto: s.auto_enabled ? "ok" : null,
  };

  return (
    <SetDirtyCtx.Provider value={report}>
      <div className="page set-page">
        <PageHeader title="Настройки" />
        <div className="set">
          <nav className="set-nav" aria-label="Разделы настроек" ref={navRef}>
            <ul className="set-nav__list">
              {SECTIONS.map((x) => {
                const on = active === x.id;
                const tone = tones[x.id];
                return (
                  <li key={x.id}>
                    <a
                      href={`#${x.id}`}
                      data-sec={x.id}
                      className={`set-nav__link ${on ? "is-active" : ""}`}
                      aria-current={on ? "location" : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        go(x.id);
                      }}
                    >
                      <x.icon className="set-nav__icon" size={18} strokeWidth={1.9} aria-hidden="true" />
                      <span className="set-nav__label">
                        {x.label}
                        {on && <EcgMark className="set-nav__pulse ecg-mark--draw" />}
                      </span>
                      {tone && (
                        <span className={`set-nav__dot set-nav__dot--${tone}`} title={TONE_TEXT[tone]}>
                          <span className="sr-only">{TONE_TEXT[tone]}</span>
                        </span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="set-main">
            <SettingsVk s={s} />
            <SettingsImages s={s} />
            <SettingsAuto s={s} />
            <SettingsClaude s={s} />
            <SettingsAccount />
          </div>
        </div>
      </div>
    </SetDirtyCtx.Provider>
  );
}

/* ================= Аккаунт ================= */
function SettingsAccount() {
  const { data: me } = useMe();
  const logout = useLogout();
  const [changing, setChanging] = useState(false);
  const user = me?.user;

  return (
    <SetSection id="account" title="Аккаунт">
      <SetBlock>
        <SetFacts
          items={[
            ["Логин", user?.login ?? "—"],
            ["Имя", user?.name || "—"],
          ]}
        />
        {changing ? (
          <PasswordForm login={user?.login ?? ""} onClose={() => setChanging(false)} />
        ) : (
          <div className="set-actions">
            <button type="button" className="btn" onClick={() => setChanging(true)}>
              <KeyRound size={17} aria-hidden="true" />
              Сменить пароль
            </button>
            {!me?.localBypass && (
              <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
                <LogOut size={17} aria-hidden="true" />
                Выйти
              </button>
            )}
          </div>
        )}
        {me?.localBypass && <p className="set-hint">С этого компьютера вход без пароля.</p>}
      </SetBlock>
    </SetSection>
  );
}

/**
 * Смена пароля. Поля монтируются только по кнопке «Сменить пароль»: иначе менеджер паролей
 * подставляет сохранённый пароль сам, и страница ложно спрашивает «Уйти без сохранения?».
 */
function PasswordForm({ login, onClose }: { login: string; onClose: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // подставленный браузером текущий пароль — не правка; правка — новый пароль
  useSetDirty("account", !!(next || repeat));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!current) return setError("Укажите текущий пароль.");
    if (next.length < 6) return setError("Новый пароль — не короче 6 символов.");
    if (next !== repeat) return setError("Новый пароль и повтор не совпадают.");
    if (next === current) return setError("Новый пароль совпадает с текущим.");
    setBusy(true);
    setError("");
    try {
      await api.put("/api/users/me/password", { current, next });
      toast("Пароль сменён. Входы на других устройствах завершены.");
      onClose();
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  };
  const field = (id: string, label: string, value: string, set: (v: string) => void, auto: string) => (
    <SetField id={id} label={label}>
      <input
        id={id}
        className="input"
        type="password"
        autoComplete={auto}
        value={value}
        onChange={(e) => {
          set(e.target.value);
          if (error) setError("");
        }}
      />
    </SetField>
  );

  return (
    <form className="set-form set-pass" onSubmit={submit} noValidate aria-label="Смена пароля">
      <input type="text" name="username" autoComplete="username" value={login} readOnly hidden />
      {field("acc-current", "Текущий пароль", current, setCurrent, "current-password")}
      {field("acc-next", "Новый пароль, от 6 символов", next, setNext, "new-password")}
      {field("acc-repeat", "Новый пароль ещё раз", repeat, setRepeat, "new-password")}
      {error && (
        <div className="error-line" role="alert">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      <div className="set-save__row">
        <button className={`btn ${current && next && repeat ? "btn--primary" : ""}`} disabled={busy || !current || !next || !repeat}>
          {busy ? "Меняю пароль…" : "Сменить пароль"}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  );
}
